import m from 'mithril';

import { requestInvite } from '../services/requestInvite';
import { captureException } from '../services/sentry';
import {
  hasNoErrors,
  validateInviteRequest,
  type FormErrors,
} from '../services/forms';
import type { InviteRequestValues } from '../types';
import { Alert } from '../components/Alert';
import { Button } from '../components/Button';
import { TextAreaField, TextField } from '../components/FormControls';
import { Type } from '../components/Typography';

/**
 * Request invite page.
 */
export const RequestInvite: m.ClosureComponent = () => {
  const state = {
    values: {
      name: '',
      email: '',
      about: '',
    } satisfies InviteRequestValues,
    errors: {} as FormErrors<InviteRequestValues>,
    submitted: false,
    submitting: false,
    error: '',
  };

  const submit = (): void => {
    state.errors = validateInviteRequest(state.values);
    state.error = '';
    if (!hasNoErrors(state.errors)) {
      return;
    }

    state.submitting = true;
    void requestInvite(state.values)
      .then(() => {
        state.submitting = false;
        state.submitted = true;
        m.redraw();
      })
      .catch((error: unknown) => {
        captureException(error);
        console.error('Failed to submit invitation request:', error);
        state.submitting = false;
        state.error = 'Failed to submit your request. Please try again later.';
        m.redraw();
      });
  };

  return {
    view: () => {
      return m('section', { class: 'page page--narrow request-page' }, [
        m('div', { class: 'form-panel' }, [
          state.submitted
            ? m(Alert, {
              title: 'Request Received!',
              message: "Thank you for your interest in MeshOS. We'll let you know when the beta is open.",
              variant: 'success',
            })
            : state.error
              ? m(Alert, {
                title: 'Something went wrong!',
                message: state.error,
                variant: 'danger',
              })
              : m('form', {
                class: 'stacked-form',
                onsubmit: (event: SubmitEvent) => {
                  event.preventDefault();
                  submit();
                },
              }, [
                m(Type, { className: 'form-intro' }, 'Request an invitation to join the MeshOS beta'),
                m(TextField, {
                  label: 'Name',
                  name: 'name',
                  placeholder: 'Your name',
                  value: state.values.name,
                  hint: state.errors.name,
                  autocomplete: 'off',
                  onInput: (value) => {
                    state.values.name = value;
                  },
                }),
                m(TextField, {
                  label: 'Email',
                  name: 'email',
                  type: 'email',
                  placeholder: 'your.email@example.com',
                  value: state.values.email,
                  hint: state.errors.email,
                  autocomplete: 'off',
                  onInput: (value) => {
                    state.values.email = value;
                  },
                }),
                m(TextAreaField, {
                  label: 'About You',
                  name: 'about',
                  placeholder: 'Tell us about yourself and your interest in productivity tools',
                  value: state.values.about,
                  hint: state.errors.about,
                  onInput: (value) => {
                    state.values.about = value;
                  },
                }),
                m(Type, { size: 'subtext' }, [
                  'If you are looking for the MeshCore LoRa radio firmware called MeshOS, please visit their official store page ',
                  m('a', {
                    href: 'https://store.meshcore.co.uk/products/meshos-meshcore-t-deck-license',
                    target: '_blank',
                    rel: 'noopener noreferrer',
                  }, 'here'),
                  '.',
                ]),
                m('div', { class: 'form-actions' }, [
                  m(Button, {
                    type: 'submit',
                    loading: state.submitting,
                    disabled: state.submitting,
                  }, 'Request Invitation'),
                ]),
              ]),
        ]),
      ]);
    },
  };
};
